import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { Target, Plus, Filter, X } from 'lucide-react';
import { apiService } from '@/services/api';
import ResearchesSidebar from './components/ResearchesSidebar';

interface SearchItem {
  id: number;
  query: string;
  status: string;
  created_at: string;
}

interface TopKeyword {
  keyword_text: string;
  volume: number;
}

interface ClusterItem {
  id: number;
  name: string;
  description?: string;
  search: number;
  created_at: string;
  updated_at: string;
  keyword_count: number;
  total_volume: number;
  avg_competition: string;
  avg_cpc: string;
  top_keywords: TopKeyword[];
}

interface ClusterKeyword {
  id: number;
  keyword: {
    id: number;
    keyword_text: string;
    volume: number;
    competition: string;
    competition_index: number;
    low_cpc: string;
    high_cpc: string;
    avg_cpc: string | null;
    low_bid_micros: number;
    high_bid_micros: number;
    avg_cpc_micros: number | null;
    da_score: number | null;
    category: number;
    category_display: string;
    created_at: string;
  };
  similarity_score: number;
  created_at: string;
}

interface ClusterDetail {
  id: number;
  name: string;
  description?: string;
  search: number;
  created_at: string;
  updated_at: string;
  keyword_count: number;
  total_volume: number;
  avg_competition: string;
  avg_cpc: string;
  cluster_keywords: ClusterKeyword[];
}

const Cluster: React.FC = () => {
  const [searches, setSearches] = useState<SearchItem[]>([]);
  const [clusters, setClusters] = useState<ClusterItem[]>([]);
  const [isLoadingSearches, setIsLoadingSearches] = useState(false);
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);
  const [selectedSearchId, setSelectedSearchId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isCreatingCluster, setIsCreatingCluster] = useState(false);
  const [clusterSearchTerm, setClusterSearchTerm] = useState<string>('');
  const [selectedCluster, setSelectedCluster] = useState<ClusterDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const fetchSearches = async () => {
    setIsLoadingSearches(true);
    setError(null);
    try {
      const response = await apiService.get('/search/list/', {
        params: {
          page: 1,
          page_size: 50, // Get more searches for the filter
        },
      });
      const searchResults = response.data.results || [];
      setSearches(searchResults);
      
    } catch (error: any) {
      console.error('Error fetching searches:', error);
      setError(error.message || 'Failed to fetch searches');
      setSearches([]);
    } finally {
      setIsLoadingSearches(false);
    }
  };

  // Fetch searches on component mount
  useEffect(() => {
    fetchSearches();
  }, []);

  const fetchClusters = useCallback(async () => {
    setIsLoadingClusters(true);
    setError(null);
    try {
      const params: any = {
        page: 1,
        page_size: 20,
      };
      
      // Add search filter if a search is selected
      if (selectedSearchId) {
        params.search_id = selectedSearchId;
      }

      // Add search term if provided
      if (clusterSearchTerm.trim()) {
        params.search = clusterSearchTerm.trim();
      }

      const response = await apiService.get('/cluster/', {
        params,
      });
      setClusters(response.data.results || []);
    } catch (error: any) {
      console.error('Error fetching clusters:', error);
      // setError(error.message || 'Failed to fetch clusters');
      setClusters([]);
    } finally {
      setIsLoadingClusters(false);
    }
  }, [selectedSearchId, clusterSearchTerm]);

  // Fetch clusters when selected search changes
  useEffect(() => {
    fetchClusters();
  }, [selectedSearchId, clusterSearchTerm, fetchClusters]);

  const getCompetitionColor = (competition: string) => {
    switch (competition) {
      case 'LOW': return 'bg-green-100 text-green-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'HIGH': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGenerateClusters = async () => {
    setIsCreatingCluster(true);
    setError(null);
    try {
      const requestData: any = {};
      
      // Include the selected search in the request
      if (selectedSearchId) {
        requestData.search_id = parseInt(selectedSearchId);
      }

      const response = await apiService.post('/cluster/', requestData);
      
      console.log('Cluster creation response:', response.data);
      
      // Refresh the clusters list after creation
      await fetchClusters();
      
    } catch (error: any) {
      console.error('Error creating clusters:', error);
      setError(error.message || 'Failed to generate clusters');
    } finally {
      setIsCreatingCluster(false);
    }
  };

  const handleViewDetails = async (clusterId: number) => {
    setIsLoadingDetail(true);
    setError(null);
    try {
      const response = await apiService.get(`/cluster/${clusterId}/`);
      
      setSelectedCluster(response.data as ClusterDetail);
      setIsDetailOpen(true);
    } catch (error: any) {
      console.error('Error fetching cluster details:', error);
      setError(error.message || 'Failed to fetch cluster details');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedCluster(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Clusters</h1>
        <p className="text-muted-foreground">
          Organize and manage keyword clusters for better analysis
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left Filter - Searches */}
        <ResearchesSidebar
          searches={searches}
          selectedSearchId={selectedSearchId}
          isLoadingSearches={isLoadingSearches}
          onSearchSelect={setSelectedSearchId}
          formatDate={formatDate}
        />

        {/* Main Content - Clusters */}
        <div className="flex-1">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-5 w-5" />
                    <span>
                      {`Clusters for "${searches.find(s => s.id.toString() === selectedSearchId)?.query || 'Unknown'}"`}
                    </span>
                  </CardTitle>
                  <CardDescription>
                    Keyword clusters generated from this research
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleGenerateClusters}
                  disabled={isCreatingCluster}
                >
                  {isCreatingCluster ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Generate Clusters
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Selected Research Display and Search Bar */}
              <div className="mb-6 space-y-4">
                {/* Selected Research Display */}
              
                
                {/* Search Bar */}
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search clusters..."
                      value={clusterSearchTerm}
                      onChange={(e) => setClusterSearchTerm(e.target.value)}
                      className="max-w-md"
                    />
                  </div>
                </div>
              </div>
              {isLoadingClusters ? (
                <div className="py-8">
                  <LoadingSpinner text="Loading clusters..." />
                </div>
              ) : clusters.length > 0 ? (
                <div className="space-y-4">
                                     {clusters.map((cluster) => (
                     <div
                       key={cluster.id}
                       className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                     >
                       {/* Cluster Header */}
                       <div className="flex items-center justify-between mb-3">
                         <div className="flex items-center space-x-3">
                           <h3 className="font-medium text-foreground">{cluster.name}</h3>
                           <Badge className={getCompetitionColor(cluster.avg_competition)}>
                             {cluster.avg_competition}
                           </Badge>
                         </div>
                         <Button 
                           variant="outline" 
                           size="sm"
                           onClick={() => handleViewDetails(cluster.id)}
                           disabled={isLoadingDetail}
                         >
                           <Filter className="mr-2 h-4 w-4" />
                           View Details
                         </Button>
                       </div>

                       {/* Cluster Description */}
                       {cluster.description && (
                         <p className="text-sm text-muted-foreground mb-3">
                           {cluster.description}
                         </p>
                       )}
                        {/* Stats and Metadata */}
                        <div className="flex items-center justify-between">
                         {/* Stats */}
                         <div className="flex items-center space-x-4 text-sm">
                           <div className="text-center">
                             <div className="font-medium text-foreground">
                               {cluster.keyword_count.toLocaleString()}
                             </div>
                             <div className="text-xs text-muted-foreground">Keywords</div>
                           </div>
                           <div className="text-center">
                             <div className="font-medium text-foreground">
                               {cluster.total_volume.toLocaleString()}
                             </div>
                             <div className="text-xs text-muted-foreground">Volume</div>
                           </div>
                           <div className="text-center">
                             <div className="font-medium text-foreground">
                               ${cluster.avg_cpc}
                             </div>
                             <div className="text-xs text-muted-foreground">Avg CPC</div>
                           </div>
                         </div>
                         
                         {/* Created Date */}
                         {/* <div className="text-sm text-muted-foreground">
                           <span>Created: {formatDate(cluster.created_at)}</span>
                         </div> */}
                       </div>
                       {/* Keywords Section */}
                       {cluster.top_keywords && cluster.top_keywords.length > 0 && (
                         <div className="mt-3">
                           {/* <h4 className="text-sm font-medium text-foreground mb-2">Top Keywords:</h4> */}
                           <div className="flex flex-wrap gap-2 items-center">
                             {cluster.top_keywords.slice(0, 5).map((keyword, index) => (
                               <Badge key={index} variant="secondary" className="text-xs">
                                 {keyword.keyword_text}
                               </Badge>
                             ))}
                             {cluster.top_keywords.length > 5 && (
                               <span className="text-xs text-muted-foreground">
                                 +{cluster.top_keywords.length - 5} more
                               </span>
                             )}
                           </div>
                         </div>
                       )}

                     </div>
                   ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Clusters Found</h3>
                  <p className="text-muted-foreground mb-4">
                    No clusters found for this research. Generate clusters to organize your keywords.
                  </p>
                  <Button 
                    onClick={handleGenerateClusters}
                    disabled={isCreatingCluster}
                  >
                    {isCreatingCluster ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Generate Clusters
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p className="font-medium">Error: {error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cluster Details Slider */}
      {isDetailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={closeDetail}
          />
          
          {/* Slider */}
          <div className="relative w-full max-w-2xl h-full bg-background border-l shadow-xl">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {selectedCluster?.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Cluster Details
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeDetail}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner text="Loading cluster details..." />
                  </div>
                ) : selectedCluster ? (
                  <div className="space-y-6">
                    {/* Cluster Info */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Cluster Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Name</label>
                            <p className="text-foreground">{selectedCluster.name}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Competition</label>
                            <Badge className={getCompetitionColor(selectedCluster.avg_competition)}>
                              {selectedCluster.avg_competition}
                            </Badge>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Total Keywords</label>
                            <p className="text-foreground">{selectedCluster.keyword_count.toLocaleString()}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Total Volume</label>
                            <p className="text-foreground">{selectedCluster.total_volume.toLocaleString()}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Average CPC</label>
                            <p className="text-foreground">${selectedCluster.avg_cpc}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Created</label>
                            <p className="text-foreground">{formatDate(selectedCluster.created_at)}</p>
                          </div>
                        </div>
                        {selectedCluster.description && (
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Description</label>
                            <p className="text-foreground">{selectedCluster.description}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Keywords List */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">
                          All Keywords ({selectedCluster.cluster_keywords?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedCluster.cluster_keywords && selectedCluster.cluster_keywords.length > 0 ? (
                          <div className="space-y-3">
                            {selectedCluster.cluster_keywords.map((clusterKeyword: ClusterKeyword, index: number) => (
                              <div 
                                key={clusterKeyword.id}
                                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <p className="font-medium text-foreground">{clusterKeyword.keyword.keyword_text}</p>
                                    <Badge variant="outline" className="text-xs">
                                      {clusterKeyword.keyword.category_display}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center space-x-4 mt-1 text-xs text-muted-foreground">
                                    <span>Similarity: {(clusterKeyword.similarity_score * 100).toFixed(1)}%</span>
                                    <span>Competition: {clusterKeyword.keyword.competition}</span>
                                    <span>CPC: ${clusterKeyword.keyword.low_cpc} - ${clusterKeyword.keyword.high_cpc}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-foreground">
                                    {clusterKeyword.keyword.volume.toLocaleString()}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Volume</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-medium text-foreground mb-2">No Keywords Found</h3>
                            <p className="text-muted-foreground">
                              This cluster doesn't have any keywords yet.
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cluster; 